import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, SectionMargins, ContactFormSectionContent } from "api/shop";
import {
  HeadingTagSelect,
  PaziaFormButton,
  PaziaInput,
  PaziaSelect,
  PaziaFileDropzone,
} from "components/atoms";
import { FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { MdAdd, MdDelete, MdClose } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";
import { ButtonVariantSelect } from "./ButtonVariantSelect";
import { useShopImageUpload } from "./useShopImageUpload";

const FORM_NAMES = {
  ANIMATION: "animation",
  AREAS: "areas",
  BUTTON_VARIANT: "buttonVariant",
} as const;

const contactSchema = yup.object({
  name: yup.string().required("Imię jest wymagane"),
  avatar: yup.mixed<string | File>().nullable(),
  phone: yup.string().default(""),
  email: yup.string().email("Nieprawidłowy email").default(""),
});

const areaSchema = yup.object({
  header: yup.string().required("Nagłówek jest wymagany"),
  headerTag: yup.string(),
  contacts: yup.array().of(contactSchema).default([]),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.AREAS]: yup.array().of(areaSchema).min(1, "Dodaj przynajmniej jeden obszar"),
    [FORM_NAMES.BUTTON_VARIANT]: yup.string().default("primary"),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormContact {
  name: string;
  avatar?: string | File | null;
  phone: string;
  email: string;
}

interface FormArea {
  header: string;
  headerTag: string;
  contacts: FormContact[];
}

interface FormValues {
  animation: string;
  areas: FormArea[];
  buttonVariant: string;
  margins: SectionMargins;
}

export interface ContactFormSectionFormValues {
  animation: string;
  areas: Array<{
    header: string;
    headerTag?: string;
    contacts: Array<{
      name: string;
      avatar?: string;
      phone?: string;
      email?: string;
    }>;
  }>;
  buttonVariant?: string;
  margins?: SectionMargins;
}

interface ContactFormSectionFormProps {
  onSubmit: (data: ContactFormSectionFormValues) => void;
  onCancel: () => void;
  defaultValues?: ContactFormSectionContent;
  isLoading?: boolean;
}

function AreaCard({ index, remove }: { index: number; remove: (i: number) => void }) {
  const { control } = useFormContext<FormValues>();

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({
    control,
    name: `areas.${index}.contacts` as const,
  });

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
          Obszar #{index + 1}
        </span>
        <button
          type="button"
          onClick={() => remove(index)}
          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
          title="Usuń obszar"
        >
          <MdDelete size={18} />
        </button>
      </div>

      <div className="grid gap-3">
        <PaziaInput
          name={`${FORM_NAMES.AREAS}.${index}.header`}
          label="Nagłówek"
          required
        />
        <HeadingTagSelect name={`${FORM_NAMES.AREAS}.${index}.headerTag`} />

        {/* Contacts */}
        <div className="rounded-md border border-slate-200 p-3 dark:border-slate-600">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Kontakty</span>
            <button
              type="button"
              onClick={() => appendContact({ name: "", avatar: null, phone: "", email: "" })}
              className="flex items-center gap-1 rounded bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={14} />
              Dodaj kontakt
            </button>
          </div>
          {contactFields.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">Brak kontaktów.</p>
          ) : (
            <div className="space-y-3">
              {contactFields.map((field, contactIndex) => (
                <div key={field.id} className="rounded-md border border-slate-200 bg-white p-3 dark:border-slate-500 dark:bg-slate-700">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                      Kontakt #{contactIndex + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeContact(contactIndex)}
                      className="rounded p-1 text-slate-400 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                      title="Usuń kontakt"
                    >
                      <MdClose size={16} />
                    </button>
                  </div>
                  <div className="grid gap-2">
                    <div className="grid gap-2 md:grid-cols-2">
                      <PaziaInput
                        name={`${FORM_NAMES.AREAS}.${index}.contacts.${contactIndex}.name`}
                        label="Imię"
                        required
                      />
                      <div className="[&_img]:max-h-20">
                        <PaziaFileDropzone
                          name={`${FORM_NAMES.AREAS}.${index}.contacts.${contactIndex}.avatar`}
                          label="Avatar"
                          type="image"
                          single
                          preview="image"
                        />
                      </div>
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                      <PaziaInput
                        name={`${FORM_NAMES.AREAS}.${index}.contacts.${contactIndex}.phone`}
                        label="Telefon"
                      />
                      <PaziaInput
                        name={`${FORM_NAMES.AREAS}.${index}.contacts.${contactIndex}.email`}
                        label="Email"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const ContactFormSectionForm = ({ onSubmit, onCancel, defaultValues, isLoading }: ContactFormSectionFormProps) => {
  const { uploadImage } = useShopImageUpload();

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.AREAS]: defaultValues?.areas?.map((area) => ({
        header: area.header,
        headerTag: area.headerTag ?? "h2",
        contacts: area.contacts?.map((c) => ({
          name: c.name,
          avatar: c.avatar ?? null,
          phone: c.phone ?? "",
          email: c.email ?? "",
        })) ?? [],
      })) ?? [],
      [FORM_NAMES.BUTTON_VARIANT]: defaultValues?.buttonVariant ?? "primary",
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control } = methods;

  const {
    fields: areaFields,
    append: appendArea,
    remove: removeArea,
  } = useFieldArray({
    control,
    name: FORM_NAMES.AREAS,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const formattedAreas = await Promise.all(
      data.areas.map(async (area) => ({
        header: area.header,
        headerTag: area.headerTag as any,
        contacts: await Promise.all(
          area.contacts.map(async (contact) => ({
            name: contact.name,
            avatar: contact.avatar ? await uploadImage(contact.avatar) : undefined,
            phone: contact.phone || undefined,
            email: contact.email || undefined,
          })),
        ),
      })),
    );

    const formattedData: ContactFormSectionFormValues = {
      animation: data.animation,
      areas: formattedAreas,
      buttonVariant: data.buttonVariant,
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addArea = () => {
    appendArea({ header: "", headerTag: "h2", contacts: [] });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaSelect name={FORM_NAMES.ANIMATION} label="Animacja" required options={ANIMATION_OPTIONS} />
        <ButtonVariantSelect name={FORM_NAMES.BUTTON_VARIANT} label="Styl przycisku formularza" />

        {/* Areas Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Obszary kontaktowe</h4>
            <button
              type="button"
              onClick={addArea}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj obszar
            </button>
          </div>

          {areaFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak obszarów. Kliknij &quot;Dodaj obszar&quot;, aby dodać.
            </p>
          ) : (
            <div className="space-y-4">
              {areaFields.map((field, index) => (
                <AreaCard key={field.id} index={index} remove={removeArea} />
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};
